// IMPORTANT: MUST NOT import from s.ts (product store) - this renderer code is used in multiple apps for image rendering

// Auto-fit for UV print lines, modeled on the engraved-plate fit
// (applyEpFitToWidth in ep-content-utils.ts): measure the text with a canvas at
// the template's point size and shrink the size until the text fits the line's
// max_width. The template pt is a hard ceiling — the fit only ever shrinks,
// never enlarges — with the same 6pt legibility floor as the EP fit (a smaller
// template pt wins over the floor so the fit can't bump text above it).
//
// Measurement space: the canvas resolves an X-pt font at 4/3 px (96 px/in) and
// UV plate-space pt is 72/inch, so measured px / 96 = plate inches — the same
// physical-CSS ratio the EP marker-span fit uses.

let sharedCtx: CanvasRenderingContext2D | null | undefined;

const getCtx = (): CanvasRenderingContext2D | null => {
    if (sharedCtx === undefined) {
        sharedCtx = typeof document !== 'undefined' ? document.createElement('canvas').getContext('2d') : null;
    }

    return sharedCtx;
};

// `weight` is the line's CSS font-weight: a bold line measures wider than the regular
// face, so the fit has to measure it as bold.
export const fitUVLinePt = (text: string, fontFamily: string, maxPt: number, maxWidthIn: number, weight: number = 400): number => {
    if (!text || !(maxPt > 0) || !(maxWidthIn > 0)) return maxPt;

    const ctx = getCtx();

    if (!ctx) return maxPt;

    const family = fontFamily || 'Arial, Helvetica, sans-serif';
    const maxWidthPx = maxWidthIn * 96;
    const fontWeight = weight > 0 ? weight : 400;
    const measure = (pt: number): number => {
        ctx.font = `${fontWeight} ${pt}pt ${family}`;

        return ctx.measureText(text).width;
    };

    const widthAtMax = measure(maxPt);

    if (widthAtMax <= maxWidthPx) return maxPt;

    const floorPt = Math.min(6, maxPt);

    // Text width scales ~linearly with font size, so start from the
    // proportional estimate (rounded down to the 0.25pt grid) and step down
    // any remaining overshoot instead of walking all the way from maxPt.
    let fontPt = Math.max(floorPt, Math.floor(maxPt * (maxWidthPx / widthAtMax) * 4) / 4);

    while (fontPt > floorPt && measure(fontPt) > maxWidthPx) {
        fontPt -= 0.25;
    }

    return Math.min(Math.max(fontPt, floorPt), maxPt);
};
